import React, { useCallback } from 'react';
import { Joyride, STATUS } from 'react-joyride';

// ── Step content helpers ─────────────────────────────────────────────────────

const TabBarContent = (
  <div style={{ fontSize: 13 }}>
    <p style={{ marginBottom: 0 }}>
      Switch between document types here: <strong>C4 Context</strong>, <strong>C4 Container</strong>,{' '}
      <strong>C4 Component</strong>, and <strong>Use Case Diagram</strong>.
    </p>
  </div>
);

const ZoomAndPanContent = (
  <div style={{ fontSize: 13 }}>
    <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.8, textAlign: 'left' }}>
      <li><strong>Scroll wheel</strong> — zoom in / out toward the cursor</li>
      <li><strong>Middle or right mouse drag</strong> — pan the diagram</li>
      <li><strong>Fit button</strong> — resets the view to fit the whole diagram</li>
      <li><strong>+ / − and slider</strong> — set the zoom level precisely</li>
    </ul>
  </div>
);

const DocIntroButtonContent = (
  <div style={{ fontSize: 13 }}>
    <p style={{ marginBottom: 0 }}>
      Reopens the documentation concepts primer (use-case &amp; C4 diagrams) at any time.
    </p>
  </div>
);

// ── Steps ────────────────────────────────────────────────────────────────────

function makeSteps(p, includeDocIntro) {
  const id = name => `#${p}${name}`;
  const steps = [
    {
      target: id('doc-viewer-root'),
      title: 'Welcome to the Design Document Viewer',
      content: (
        <div style={{ fontSize: 13 }}>
          <p>This viewer shows the <strong>design documentation diagrams</strong> for your assigned target.</p>
          <p style={{ marginBottom: 0 }}>Use it to understand the system's structure before testing.</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: id('doc-tab-bar'),
      title: 'Document Tabs',
      content: TabBarContent,
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: id('doc-zoom-controls'),
      title: 'Zoom & Pan Options',
      content: ZoomAndPanContent,
      placement: 'bottom',
      disableBeacon: true,
    },
  ];
  if (includeDocIntro) {
    steps.push({
      target: id('doc-intro-btn'),
      title: 'Doc Intro Button',
      content: DocIntroButtonContent,
      placement: 'bottom',
      disableBeacon: true,
    });
  }
  return steps;
}

// ── Styles ───────────────────────────────────────────────────────────────────
// Same shape/behavior/colors as TmViewerTourJoyride, so the tour itself reads
// as one consistent feature regardless of which viewer it's shown in.

const joyrideOptions = {
  primaryColor: '#5e35b1',
  zIndex: 10000,
  width: 420,
};

const joyrideStyles = {
  tooltip: {
    borderRadius: 8,
    padding: '16px 20px',
  },
  tooltipTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 8,
  },
  tooltipContent: {
    fontSize: 13,
    padding: 0,
  },
  buttonNext: {
    borderRadius: 6,
    fontSize: 13,
    padding: '6px 16px',
  },
  buttonBack: {
    borderRadius: 6,
    fontSize: 13,
    padding: '6px 14px',
    color: '#5e35b1',
  },
  buttonSkip: {
    fontSize: 12,
    color: '#999',
  },
};

// ── Component ────────────────────────────────────────────────────────────────

export default function DocViewerTourJoyride({ run, onFinish, idPrefix = '', includeDocIntro = true }) {
  const steps = makeSteps(idPrefix, includeDocIntro);
  const handleCallback = useCallback((data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onFinish();
    }
  }, [onFinish]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep={false}
      disableScrolling
      spotlightPadding={6}
      options={joyrideOptions}
      styles={joyrideStyles}
      locale={{
        back: '← Back',
        close: 'Close',
        last: 'Got it — start exploring',
        next: 'Next →',
        skip: 'Skip tour',
      }}
      callback={handleCallback}
    />
  );
}
